/* ─────────────────────────────────────────────────────────────────────────────
   Settings → Setup Pickup Drop Mapping — API calls (merchant panel).

   Backend: proship_mongo /api/pickup-drop-mapping/*. Every call carries the merchant the panel is
   acting as (getMerchant().id from Redux): for a merchant user that is their own merchant and the
   backend accepts it as such; for a Proship admin using the merchant panel it is required, since
   the backend refuses to guess which merchant an admin token means. JSON responses use the
   GenericAPIResponse envelope — the payload is `responseObj`; CSV endpoints stream the file.

     GET    config                         → { enabled, defaultPickupPincode, mappings[{dropType, dropValue, pickupPincode}], lastUploadFileName, lastUploadAt, ... }
     GET    states                         → ["Andhra Pradesh", ...]   exact spellings accepted in a mapping
     GET    pickup-pincodes                → ["110001", ...]           the merchant's saved pickup locations (hints only)
     GET    template                       → CSV (every state, blank pickup; example in the header text)
     GET    download                       → CSV of the current rules (re-uploadable)
     GET    pickup?dropPincode=            → { found, pickupPincode, matchedBy, matchedDrop, dropState, dropCity, message }
     PUT    settings {enabled, defaultPickupPincode}   (a key left out of the body is left unchanged)
     POST   upload?confirm= (file)         → { confirmed, rowsInFile, validRows, added, changed, unchanged, blankPickupRows, existingMappings, invalidRows[] }
     POST   mapping {drop, pickupPincode}  → config
     DELETE mapping?drop=                  → config
     (DELETE mappings — clear all — is admin-panel only; not exposed to merchants)
   ──────────────────────────────────────────────────────────────────────────── */
import { apiConstants, deleteGeneric, getGeneric, getGenericBlob, postGeneric, putGeneric } from '../../common/constants'
import { getMerchant } from '../../helpers/ReduxHelpers'

const BASE = apiConstants.PICKUP_DROP_MAPPING

/** The merchant this panel session is acting as; '' when the store has none yet. */
const merchantOid = () => getMerchant()?.id || ''

/** Query string for the merchant plus any extra params, dropping empty values. */
const qs = (extra = {}) => {
  const params = new URLSearchParams()
  Object.entries({ merchantOid: merchantOid(), ...extra }).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params.set(k, v)
  })
  return params.toString()
}

/** Unwraps the GenericAPIResponse envelope. */
const body = (r) => r.data?.responseObj

export const fetchConfig = () => getGeneric(`${BASE}/config?${qs()}`).then(body)

export const fetchStates = () => getGeneric(`${BASE}/states`).then((r) => body(r) || [])

export const fetchPickupPincodes = () => getGeneric(`${BASE}/pickup-pincodes?${qs()}`).then((r) => body(r) || [])

export const resolvePickup = (dropPincode) => getGeneric(`${BASE}/pickup?${qs({ dropPincode })}`).then(body)

export const saveSettings = (settings) => putGeneric(`${BASE}/settings?${qs()}`, settings).then(body)

export const addMapping = (row) => postGeneric(`${BASE}/mapping?${qs()}`, row).then(body)

export const deleteMapping = (drop) => deleteGeneric(`${BASE}/mapping?${qs({ drop })}`).then(body)

export const downloadTemplate = () => getGenericBlob(`${BASE}/template`)

export const downloadMappings = () => getGenericBlob(`${BASE}/download?${qs()}`)

export const uploadMappings = ({ file, confirm }) => {
  const form = new FormData()
  form.append('file', file)
  return postGeneric(`${BASE}/upload?${qs({ confirm: confirm ? 'true' : 'false' })}`, form, { headers: { 'Content-Type': 'multipart/form-data' } }).then(body)
}

export const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** Server message for a failed call (validation texts are written for the user, so show them as is). */
export const errorText = (e, fallback = 'Something went wrong') => e?.response?.data?.message || e?.message || fallback
